import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import window from 'window-or-global';
import { nativeEditorComponentsPreloader } from '@confluence-classic/confluence-fabric-editor/src/lib';
import { bind, unbind } from '@confluence-classic/confluence-event_DO_NOT_USE';

import {
	CREATE_PAGE_LOAD_DIALOG_EXPERIENCE,
	// WARN: disabling only to meet ESlint requirements during ESlint upgrade
	getExperienceTracker, // eslint-disable-line no-restricted-imports
} from '@confluence/experience-tracker';
import { Attribution } from '@confluence/error-boundary';
import { getMonitoringClient } from '@confluence/monitoring';
import { requireLegacyWRM } from '@confluence/wrm';

import waitForGlobal from '../../lib/wait-for-global-PLEASE-AVOID-USING';
import waitForRequiredModule from '../../lib/wait-for-required-module-PLEASE-AVOID-USING';
import { globalRequire } from '../../tech-debt/facades/window';

export class PseudoCreateDialogComponent extends PureComponent {
	static displayName = 'PseudoCreateDialog';
	static propTypes = {
		spaceKey: PropTypes.string,
		shouldSuppressDialog: PropTypes.bool,
		onClose: PropTypes.func,
		open: PropTypes.bool,
		//The ID of the parent page under which to create a page. Leave blank for
		//current page.
		parentPageId: PropTypes.string,
		parentPageTitle: PropTypes.string,
		parentPageType: PropTypes.string,
		parentPageSpaceKey: PropTypes.string,
		spaTransitionEnabled: PropTypes.bool,
	};

	static defaultProps = {
		onClose: () => {},
	};

	componentDidMount() {
		bind('remove.dialog', this._afterDialogClosed);
		this.forceReloadInNextOpen = true;
		this._setOpenState(this.props.open);
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		if (this.props.spaceKey !== nextProps.spaceKey) {
			this.forceReloadInNextOpen = true;
		}
		if (nextProps.open) {
			nativeEditorComponentsPreloader();
		}
	}

	componentWillUnmount() {
		if (this.props.open) {
			this._close();
		}
		unbind('remove.dialog', this._afterDialogClosed);
	}

	isOpening = false;

	forceReloadInNextOpen = true;

	_setOpenState = (openState) => {
		if (openState) {
			this._open();
		} else {
			this._close();
		}
	};

	/**
	 * Close the create dialog
	 * @private
	 */
	_close = () => {
		// Destroy the dialog
		if (typeof this._confluenceDialogClose === 'function') {
			// a defensive way to make sure legacy code should not break our React code due to unknown error.
			try {
				this._confluenceDialogClose();
			} catch (error) {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.TAILORED_EXPERIENCES,
				});
				if (process.env.NODE_ENV !== 'production') {
					console.error('Failing in attempt to close create page dialog: ', error);
				}
			}

			this._confluenceDialogClose = null;
		}
	};

	/**
	 * Triggered after dialog closed
	 * @private
	 */
	_afterDialogClosed = (e, { dialog }) => {
		if (dialog.id === 'create-dialog') {
			this._confluenceDialogClose = null;
			this._close(e);
			this.props.onClose();
		}
	};

	_isActive = () => !!window.document.querySelector('#create-dialog');

	_open = () => {
		if (!this._isActive() && !this.isOpening) {
			this.isOpening = true;

			requireLegacyWRM(
				[
					'wrc!com.atlassian.confluence.plugins.confluence-create-content-plugin:resources',
					'wrc!com.atlassian.confluence.plugins.confluence-business-blueprints:meeting-notes-resources',
					'wrc!com.atlassian.plugins.atlassian-connect-plugin:blueprint-css-resources',
					'wrc!com.atlassian.plugins.atlassian-connect-plugin:icons-css-resources',
					'wrc!com.atlassian.plugins.atlassian-connect-plugin:dynamic-css-resources',
				],
				() => {
					// Although we've included 'wr!com.atlassian.plugins.atlassian-connect-plugin:dynamic-css-resources' here
					// The 'link-style-css' may have been executed somewhere else previously.
					// Requiring it again will not have effect. Here we are going to manually initialize it.
					waitForRequiredModule(
						{
							listener: 'PseudoCreateDialog',
							module: 'ac/confluence/style/link-style-css',
						},
						() => {
							const linkStyleCSS = globalRequire('ac/confluence/style/link-style-css');
							if (linkStyleCSS && typeof linkStyleCSS.addLinkTags === 'function') {
								linkStyleCSS.addLinkTags();
							}
						},
					);

					const { shouldSuppressDialog, spaTransitionEnabled } = this.props;
					if (!shouldSuppressDialog) {
						waitForGlobal(
							{
								listener: 'PseudoCreateDialog',
								globalProperty: 'Confluence.Blueprint.loadDialog',
							},
							() => {
								const { parentPageId, parentPageTitle, parentPageType, parentPageSpaceKey } =
									this.props;

								window.Confluence.Blueprint.loadDialog({
									triggerSrc: 'create-page',
									forceReload: this.forceReloadInNextOpen,
									parentPageId,
									parentPageTitle,
									parentPageType,
									parentPageSpaceKey,
									spaTransitionEnabled,
								}).then((dialog) => {
									this.isOpening = false;
									this.forceReloadInNextOpen = false;
									this._confluenceDialogClose = dialog.closeDialog;
									getExperienceTracker().succeed({
										name: CREATE_PAGE_LOAD_DIALOG_EXPERIENCE,
									});
								});
							},
						);
					}
				},
				() => {
					if (process.env.NODE_ENV !== 'production') {
						console.error('Pseudo create dialog cannot be initialized when WRM is disabled.');
					}
				},
			);
		}
	};

	render() {
		return null;
	}
}
